import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported, logEvent } from "firebase/analytics";
import { getFirestore } from "firebase/firestore";
import { devError, devWarn } from "../utils/devLog";

// Firebase Console → Project settings → Your apps → Web app SDK snippet.
// CRA reads REACT_APP_* from .env at build time (see .env.example).
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY?.trim(),
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN?.trim(),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID?.trim(),
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET?.trim(),
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID?.trim(),
  appId: process.env.REACT_APP_FIREBASE_APP_ID?.trim(),
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID?.trim(),
};

const hasFirebaseConfig = Boolean(
  firebaseConfig.apiKey && firebaseConfig.appId && firebaseConfig.projectId
);

const hasAnalyticsConfig = Boolean(
  hasFirebaseConfig && firebaseConfig.measurementId
);

let firebaseApp = null;
let analyticsPromise = Promise.resolve(null);

const getFirebaseApp = () => {
  if (!hasFirebaseConfig || typeof window === "undefined") {
    return null;
  }

  if (!firebaseApp) {
    firebaseApp = initializeApp(firebaseConfig);
  }

  return firebaseApp;
};

export const getFirestoreDb = () => {
  const app = getFirebaseApp();
  if (!app) {
    return null;
  }
  return getFirestore(app);
};

const initFirebaseAnalytics = () => {
  if (!hasAnalyticsConfig || typeof window === "undefined") {
    if (process.env.NODE_ENV === "development" && !hasAnalyticsConfig) {
      devWarn(
        "[Monitoring] Firebase Analytics disabled — set REACT_APP_FIREBASE_* in .env and restart."
      );
    }
    return Promise.resolve(null);
  }

  try {
    const app = getFirebaseApp();
    if (!app) {
      return Promise.resolve(null);
    }

    analyticsPromise = isSupported()
      .then((supported) => (supported ? getAnalytics(app) : null))
      .catch((error) => {
        devError("[Monitoring] Firebase Analytics is not available", error);
        return null;
      });
    return analyticsPromise;
  } catch (error) {
    devError("[Monitoring] Firebase failed to initialize", error);
    analyticsPromise = Promise.resolve(null);
    return analyticsPromise;
  }
};

export const initMonitoring = () => {
  getFirebaseApp();
  initFirebaseAnalytics();
};

export const logAnalyticsEvent = (eventName, params = {}) => {
  analyticsPromise
    .then((analytics) => {
      if (!analytics) {
        return;
      }
      logEvent(analytics, eventName, params);
    })
    .catch((error) => {
      devError("[Monitoring] Failed to log analytics event", error);
    });
};
